import React, { useEffect, useState } from "react";
import { Star, Check, EyeOff, Trash2, BadgeCheck, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const STATUS = [["all", "All"], ["pending", "Pending"], ["approved", "Published"], ["hidden", "Hidden"]];
const BADGE = { pending: "bg-[#fff4dc] text-[#8a5a00]", approved: "bg-[#e3f6f1] text-[#0b6f65]", hidden: "bg-[#eef1f1] text-[#5b6d6a]" };
const chip = (on) => `rounded-full px-3 py-1.5 text-[13px] font-semibold transition-colors ${on ? "bg-[#0f2e2a] text-white" : "border border-[#dcebe8] bg-white text-[#4b5d5a] hover:bg-[#f2f7f6]"}`;
const btn = "inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[13px] font-semibold transition-colors";

const ReviewCard = ({ r, owner, act }) => (
  <div className="rounded-2xl border border-[#e3f0ed] bg-white p-4" data-testid={`review-card-${r.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="font-semibold text-[#0f2e2a]">{r.name}{r.location && <span className="font-normal text-[#6b7c79]"> · {r.location}</span>}</span>
      <span className="flex items-center gap-2">
        <span className="flex text-[#f59e0b]">{[...Array(r.rating)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${BADGE[r.status] || BADGE.hidden}`} data-testid={`review-status-${r.id}`}>{r.status === "approved" ? "Published" : r.status[0].toUpperCase() + r.status.slice(1)}</span>
      </span>
    </div>
    <p className="mt-2 text-[14px] leading-relaxed text-[#2f4441]">“{r.text}”</p>
    <div className="mt-1 flex items-center gap-1 text-[12px] text-[#8a9a97]"><BadgeCheck size={13} className="text-[#0e8c7f]" />Verified customer · {new Date(r.created_at).toLocaleDateString()}</div>
    {owner && (
      <div className="mt-3 flex flex-wrap gap-2">
        {r.status !== "approved" && <button onClick={() => act(r, "approve")} className={`${btn} bg-[#0e8c7f] text-white hover:bg-[#0b7569]`} data-testid={`review-publish-${r.id}`}><Check size={14} />Publish</button>}
        {r.status === "approved" && <button onClick={() => act(r, "hide")} className={`${btn} border border-[#dcebe8] text-[#4b5d5a] hover:bg-[#f4f8f7]`} data-testid={`review-hide-${r.id}`}><EyeOff size={14} />Hide</button>}
        {r.status === "approved" && ["square", "story"].map((sz) => <button key={sz} onClick={() => act(r, sz)} className={`${btn} border border-[#cfe0f7] text-[#1d5fb3] hover:bg-[#f2f7ff]`} data-testid={`review-share-${sz}-${r.id}`}><Download size={14} />{sz === "square" ? "Facebook image" : "Story image"}</button>)}
        <button onClick={() => act(r, "delete")} className={`${btn} border border-[#f1d4d4] text-[#b42318] hover:bg-[#fff3f2]`} data-testid={`review-delete-${r.id}`}><Trash2 size={14} />Delete</button>
      </div>
    )}
  </div>
);

const ReviewsTab = ({ owner }) => {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("all");
  const [stars, setStars] = useState(0);
  const load = () => api.get("/admin/reviews").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const act = async (r, a) => {
    if (a === "delete" && !window.confirm("Delete this review permanently?")) return;
    try {
      if (a === "square" || a === "story") {
        const { data } = await api.get(`/admin/reviews/${r.id}/share?size=${a}`, { responseType: "blob" });
        const url = URL.createObjectURL(data);
        const el = document.createElement("a");
        el.href = url; el.download = `stripwise-review-${r.name.split(" ")[0].toLowerCase()}-${a}.jpg`; el.click();
        URL.revokeObjectURL(url);
        return toast.success("Image downloaded — ready to post");
      }
      let res;
      if (a === "delete") await api.delete(`/admin/reviews/${r.id}`);
      else res = await api.post(`/admin/reviews/${r.id}/${a}`);
      toast.success(a === "approve" ? (res?.data?.thanked ? "Review published — thank-you email sent" : "Review published on the website") : a === "hide" ? "Review hidden from the website" : "Review deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
  };
  if (!items) return null;
  const list = items.filter((r) => (status === "all" || r.status === status) && (!stars || r.rating === stars));
  return (
    <div className="max-w-5xl space-y-4" data-testid="admin-reviews-tab">
      <p className="text-[14px] text-[#5b6d6a]">Reviews from paid customers (via the email link or the public <b>/leave-review</b> page). Published reviews appear on your website with a "Verified customer" badge.</p>
      <div className="flex flex-wrap items-center gap-2">
        {STATUS.map(([k, l]) => <button key={k} onClick={() => setStatus(k)} className={chip(status === k)} data-testid={`reviews-filter-${k}`}>{l} ({k === "all" ? items.length : items.filter((r) => r.status === k).length})</button>)}
        <span className="mx-1 h-5 w-px bg-[#dcebe8]" />
        {[0, 5, 4, 3, 2, 1].map((s) => <button key={s} onClick={() => setStars(s)} className={chip(stars === s)} data-testid={`reviews-stars-${s}`}>{s ? `${s}★` : "All stars"}</button>)}
      </div>
      {list.length ? <div className="grid gap-3 lg:grid-cols-2">{list.map((r) => <ReviewCard key={r.id} r={r} owner={owner} act={act} />)}</div>
        : <p className="rounded-2xl border border-dashed border-[#dcebe8] p-8 text-center text-[14px] text-[#6b7c79]" data-testid="reviews-empty">No reviews here yet.</p>}
    </div>
  );
};

export default ReviewsTab;
